<template>
  <v-container class="modern-consume-view pb-12">
    <!-- Premium Global Header (Master Pattern) -->
    <v-row class="mb-6 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="44" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white" small>fas fa-flask</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text d-flex align-center lh-1">
            Bitácora de Consumo
          </div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Monitoreo y trazabilidad de insumos utilizados</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center">
        <export-consume-bitacora :filter="filter" :options="options" :disabled="loading" :search="search"/>
      </div>
    </v-row>

    <!-- Filter Section (Master Pattern) -->
    <v-card outlined class="modern-card pa-6 mb-8 shadow-sm">
      <v-row dense align="end">
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Desde</div>
          <v-text-field 
            v-model='filter.created_after' 
            outlined dense hide-details="auto"
            type="date" 
            :disabled="loading" 
            clearable 
            class="modern-input"
            @change="triggerFetch"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Hasta</div>
          <v-text-field 
            v-model='filter.created_before' 
            outlined dense hide-details="auto"
            type="date" 
            :disabled="loading" 
            clearable 
            class="modern-input"
            @change="triggerFetch"
          />
        </v-col>
        <v-col cols="12" md="6">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Grupo de artículos</div>
          <v-select
            v-model="filter.family"
            :items="families"
            :loading="familiesLoading"
            item-text="name"
            item-value="id"
            placeholder="Todos los grupos"
            outlined dense hide-details="auto"
            clearable
            class="modern-select"
            @change="triggerFetch"
          />
        </v-col>
      </v-row>
    </v-card>

    <!-- Main Results Section -->
    <v-card outlined class="modern-card overflow-hidden shadow-sm">
      <v-data-table
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="totalItems"
        :loading="loading"
        class="modern-table"
        @update:options="fetch"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 30, 40, 50, 100],
        }"
      >
        <!-- Search Bar in Top Slot (Master Pattern) -->
        <template v-slot:top>
          <div class="px-6 py-4 border-bottom d-flex align-center bg-white">
            <v-text-field
              v-model="search"
              placeholder="Buscar por OT, Artículo o SKU..."
              prepend-inner-icon="fas fa-search"
              outlined dense hide-details="auto"
              clearable
              :disabled="loading"
              class="modern-search-field flex-grow-1"
              @keyup="searchTimeOut"
            />
          </div>
        </template>
        
        <template v-slot:item.created_at="{ item }">
          <div class="caption font-weight-bold grey--text">{{item.created_at | moment("DD/MM/YYYY")}}</div>
        </template>
        
        <template v-slot:item.workorder="{item}">
          <show-work-order :work_order_id="item.product.work_order" class="text-decoration-none">
            <div class="caption grey--text mb-n1">{{item.product.customer_name}}</div>
            <div class="primary--text font-weight-black">OT {{item.product.work_order}}</div>
          </show-work-order>
        </template>
        
        <template v-slot:item.product="{item}">
          <div class="caption grey--text uppercase tracking-tighter mb-n1">[{{item.product.sku}}]</div>
          <div class="caption font-weight-bold text-truncate" style="max-width: 200px">{{item.product.name}}</div>
        </template>
        
        <template v-slot:item.article="{item}">
          <v-chip x-small color="grey lighten-4" label class="mb-1 font-weight-black">SKU: {{item.article.sku}}</v-chip>
          <div class="caption font-weight-medium lh-1-2">{{item.article.name}}</div>
        </template>
        
        <template v-slot:item.articlePrice="{item}">
          <span class="caption font-weight-bold">{{item.articlePrice | moneda}}</span>
        </template>
        
        <template v-slot:item.convertedPrice="{item}">
          <div class="caption secondary--text font-weight-black">
            {{item.convertedPrice | moneda}}
            <span class="grey--text font-weight-medium" style="font-size: 0.6rem"> (x{{item.unitConsume_factor|number('0.0000') }})</span>
          </div>
        </template>
        
        <template v-slot:item.qty="{ item }">
          <div class="text-end">
            <div class="font-weight-black">{{item.qty | number('0,0.00')}}</div>
            <v-icon class="error--text" x-small v-if="item.reprocess">fas fa-clock-rotate-left</v-icon>
          </div>
        </template>
        
        <template v-slot:item.total="{ item }">
           <div class="text-end font-weight-black primary--text">{{item.total | moneda}}</div>
        </template>
        
        <template v-slot:item.created_by__first_name="{ item }">
          <v-tooltip left content-class="premium-tooltip">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" class="caption font-weight-bold grey--text">
                {{item.created_by_full_name}}
              </div>
            </template>
            <div class="pa-1 caption white--text">{{item.notes || 'Sin observaciones'}}</div>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import {fetchFamilies} from '@/api/stocks'
import {fetchConsumtionBitacora} from '@/api/production'
import ExportConsumeBitacora from '@/components/reports/ExportConsumeBitacora'
import notifications from '@/utils/notifications'
import moment from 'moment'

export default {
  name:'ConsumeBitacora',
  components:{ExportConsumeBitacora},
  data() {
    return {
      loading:false,
      filter:{
        created_after:moment(new Date(new Date().getFullYear(), new Date().getMonth(), 1)).format('yyyy-MM-DD'),
        created_before:'',
        family:'',
        },
      options:{},
      totalItems:0,
      display:false,
      search:'',
      items:[],
      families:[],
      familiesLoading:false,
      headers:[
          {text: 'Creada',align: 'start',value: 'created_at',class: 'table-header2',divider:true},
          {text: 'Cliente | OT',align: 'start',value: 'workorder',class: 'table-header2',divider:true,width: '12%'},
          {text: 'Producto',align: 'start',value: 'product',class: 'table-header2',divider:true,width: '12%'},
          {text: 'Artículo Consumido',align: 'start',value: 'article',class: 'table-header2'},
          {text: 'Unid.',align: 'start',value: 'unit',class: 'table-header2',sortable:true},
          {text: 'Precio Prom.',align: 'end',value: 'articlePrice',class: 'articlePrice',sortable:true},
          {text: 'Consumo (Conv.)',align: 'start',value: 'unitConsume',class: 'table-header2',sortable:false,divider:true},
          {text: 'Prec. Conv.',align: 'end',value: 'convertedPrice',class: 'table-header2',sortable:false},
          {text:'Cant.',align:'end',value:'qty',class:'table-header2',sortable:true},
          {text:'Total',align:'end',value:'total',class:'table-header2',sortable:true},
          {text:'Usuario',align:'end',value:'created_by__first_name',class:'table-header2',sortable:true},
        ]
    }
  },
  methods:{
    triggerFetch() {
      this.options.page = 1
      this.fetch()
    },
    async fetch(){
      this.loading=true
      var created_before = ''
      var created_after = ''
      if(this.filter.created_after) created_after = moment(this.filter.created_after).utc().format('yyyy-MM-DD HH:mm')
      if(this.filter.created_before) created_before=moment(this.filter.created_before).utc().add(1439,'minutes').format('yyyy-MM-DD HH:mm')

      if(!this.filter.family) this.filter.family=''
      
      const {sortBy, sortDesc,page, itemsPerPage }=this.options
      const query=`ordering=${sortDesc[0]?sortBy[0]:'-'+sortBy[0]}&page=${page}&page_size=${itemsPerPage}&search=${this.search}&created_after=${created_after}&created_before=${created_before}&article__family=${this.filter.family}`
      await fetchConsumtionBitacora(query)
      .then((data)=>{
        this.totalItems=data.count
        this.items = data.results
      })
      .catch((err)=>notifications.showError(err))
      .finally(()=>this.loading=false)
    },
    searchTimeOut() {  
      if (this.timer) {
          clearTimeout(this.timer);
          this.timer = null;
      }
      this.timer = setTimeout(() => {
          this.fetch()
      }, 800);
    },
  },
  async mounted(){
    await this.fetch()
    this.familiesLoading=true
    this.families=await fetchFamilies()
    this.familiesLoading=false
  },
  computed:{
      equipments(){
        return this.filter.family?this.filter.family.equipments:[]
      }
    }
}
</script>

<style scoped>
.modern-consume-view { background-color: #f4f7f9; min-height: 100vh; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.shadow-sm { box-shadow: 0 2px 12px rgba(0,0,0,0.04) !important; }
.border-white-sm { border: 2px solid white !important; }
.modern-input >>> .v-input__control fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }
.modern-select >>> .v-input__control fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }
.modern-search-field >>> .v-input__control fieldset { border-radius: 12px !important; border-color: #e0e4e8 !important; background-color: #f8fbfc !important; }
.modern-search-field >>> .v-input__prepend-inner { margin-top: 2px !important; margin-right: 8px !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.2 !important; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 2px; }
.tracking-tighter { letter-spacing: -0.5px; }
.gap-3 { gap: 12px; }
.opacity-70 { opacity: 0.7; }

.modern-table >>> .v-data-table-header th { white-space: nowrap; height: 56px !important; text-transform: uppercase; font-size: 0.7rem !important; letter-spacing: 1.5px; font-weight: 900 !important; background-color: #f8fbfc !important; color: #607d8b !important; }
.modern-table >>> td { padding: 12px 16px !important; border-bottom: 1px solid #f0f3f5 !important; }
.modern-table >>> tr:hover { background-color: rgba(var(--v-primary-base), 0.02) !important; }

.premium-tooltip { background: rgba(33, 33, 33, 0.95) !important; backdrop-filter: blur(4px); border-radius: 12px !important; border: 1px solid rgba(255,255,255,0.1); }
</style>
